// src/components/SessionExpirationWarning.js
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useAuth } from "../contexts/AuthContext";
import logger from "../utils/logger";
import "./SessionExpirationWarning.css";

const SessionExpirationWarning = () => {
  const { getTokenExpiresAt, getAccessToken, logout } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());
  const [devStreamOffset, setDevStreamOffset] = useState(0); // Track DevStream pane width for positioning
  const isDismissedRef = useRef(false);
  const hasLoggedOutRef = useRef(false); // Track if logout has been called

  // Create portal root for rendering outside modal DOM hierarchy
  const [portalRoot] = useState(() => {
    const div = document.createElement("div");
    div.setAttribute("id", "session-warning-portal");
    return div;
  });

  // Keep ref in sync with state
  useEffect(() => {
    isDismissedRef.current = isDismissed;
  }, [isDismissed]);

  // Mount portal root to document.body
  useEffect(() => {
    document.body.appendChild(portalRoot);
    return () => {
      if (document.body.contains(portalRoot)) {
        document.body.removeChild(portalRoot);
      }
    };
  }, [portalRoot]);

  // Monitor DevStream pane visibility and width for positioning
  useEffect(() => {
    const updateDevStreamOffset = () => {
      const devStreamVisible =
        localStorage.getItem("devStreamsVisible") === "true";
      if (devStreamVisible) {
        // Get DevStream pane width percentage from localStorage (default 40%)
        const paneWidthPercent =
          parseFloat(localStorage.getItem("devStream_paneWidth")) || 40;
        const viewportWidth = window.innerWidth;
        const paneWidthPx = (viewportWidth * paneWidthPercent) / 100;
        // Add 30px padding to prevent overlap with pane border
        setDevStreamOffset(paneWidthPx + 30);
      } else {
        setDevStreamOffset(0);
      }
    };

    // Update on mount
    updateDevStreamOffset();

    // Listen for localStorage changes (DevStream visibility toggled)
    window.addEventListener("storage", updateDevStreamOffset);
    // Listen for resize events to recalculate percentage-based width
    window.addEventListener("resize", updateDevStreamOffset);

    // Poll for changes since localStorage events don't fire in same tab
    const interval = setInterval(updateDevStreamOffset, 1000);

    return () => {
      window.removeEventListener("storage", updateDevStreamOffset);
      window.removeEventListener("resize", updateDevStreamOffset);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const updateTimer = () => {
      const expiresAt = getTokenExpiresAt();
      if (!expiresAt) {
        setTimeRemaining(null);
        return;
      }

      const now = Date.now();
      const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));

      // Update time remaining first
      setTimeRemaining(remaining);

      // Log when approaching critical thresholds
      if (remaining <= 300 && remaining > 0) {
        logger.log(
          `[SessionExpirationWarning] Timer update: ${remaining} seconds remaining`
        );
      }

      // Force logout when timer reaches 0 (only once)
      if (remaining === 0 && !hasLoggedOutRef.current) {
        logger.warn(
          "[SessionExpirationWarning] Token expired. Forcing logout..."
        );
        hasLoggedOutRef.current = true;
        logout("session_expired");
        return;
      }

      // Auto-dismiss if token was refreshed (time jumped back up)
      if (remaining > 300) {
        // More than 5 minutes - hide warning and reset dismiss state
        setIsDismissed(false);
        hasLoggedOutRef.current = false; // Reset logout flag if session is extended
        return;
      }

      // Re-show at 10 seconds if previously dismissed and user hasn't refreshed
      if (remaining <= 10 && isDismissedRef.current) {
        setIsDismissed(false);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    // Listen for token refresh events and user activity
    const handleTokenRefresh = (e) => {
      const newExpiresAt = e.detail?.expiresAt;
      logger.log(
        "[SessionExpirationWarning] ✅✅✅ tokenRefreshed event RECEIVED! new expiresAt:",
        newExpiresAt,
        "new Date:",
        new Date(newExpiresAt)
      );
      setIsDismissed(false);
      setLastActivityTime(Date.now());
      // Force immediate update
      setTimeout(updateTimer, 0);
    };

    const handleUserActivity = () => {
      logger.log("[SessionExpirationWarning] userActivity event received");
      // User performed an action - record activity time and temporarily dismiss
      setLastActivityTime(Date.now());
      setIsDismissed(true);
      // Update timer after a brief delay
      setTimeout(updateTimer, 100);
    };

    logger.log("[SessionExpirationWarning] Setting up event listeners");
    window.addEventListener("tokenRefreshed", handleTokenRefresh);
    window.addEventListener("userActivity", handleUserActivity);
    window.addEventListener("storage", updateTimer);

    return () => {
      clearInterval(interval);
      window.removeEventListener("tokenRefreshed", handleTokenRefresh);
      window.removeEventListener("userActivity", handleUserActivity);
      window.removeEventListener("storage", updateTimer);
    };
  }, [getTokenExpiresAt, logout]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await getAccessToken();
      // Token will be refreshed and tokenRefreshed event will be dispatched
      // The event listener will handle dismissing the warning
    } catch (error) {
      logger.error(
        "[SessionExpirationWarning] Failed to refresh token:",
        error
      );
      alert("Failed to refresh session. Please log in again.");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Don't show if:
  // - No time remaining data
  // - More than 5 minutes left
  // - User dismissed it (but force show at 10 seconds - handled in updateTimer)
  // - Recent user activity (within 30 seconds) and more than 2 minutes remaining
  const timeSinceActivity = Date.now() - lastActivityTime;
  const thirtySecondsInMs = 30 * 1000;
  const hasRecentActivity = timeSinceActivity < thirtySecondsInMs;

  // Debug logging to diagnose visibility issues (only when showing/hiding)
  const shouldShow = !(
    timeRemaining === null ||
    timeRemaining > 300 ||
    isDismissed ||
    (hasRecentActivity && timeRemaining > 120)
  );

  // Log visibility decision when time is critical (< 5 min) - disabled to reduce console noise
  // useEffect(() => {
  //   if (timeRemaining !== null && timeRemaining <= 300) {
  //     logger.log("[SessionExpirationWarning] Visibility check:", {
  //       timeRemaining,
  //       isDismissed,
  //       timeSinceActivitySeconds: Math.floor(timeSinceActivity / 1000),
  //       hasRecentActivity,
  //       shouldShow,
  //     });
  //   }
  // }, [
  //   shouldShow,
  //   timeRemaining,
  //   isDismissed,
  //   timeSinceActivity,
  //   hasRecentActivity,
  // ]);

  if (!shouldShow) {
    return null;
  }

  // Render using portal to ensure warning appears above modals
  return ReactDOM.createPortal(
    <div
      className="session-expiration-warning"
      style={{ right: `${20 + devStreamOffset}px` }}
    >
      <div className="warning-content">
        <span className="warning-icon">⚠️</span>
        <div className="warning-text">
          <div className="warning-title">Session Expiring Soon</div>
          <div className="warning-message">
            Your session will end in{" "}
            <strong>{formatTime(timeRemaining)}</strong> if inactive
          </div>
        </div>
      </div>
      <button
        className="warning-refresh"
        onClick={handleRefresh}
        disabled={isRefreshing}
        aria-label="Refresh session"
        title="Refresh session"
      >
        {isRefreshing ? "⟳" : "✨"}
      </button>
      <button
        className="warning-close"
        onClick={handleDismiss}
        aria-label="Dismiss warning"
        title="Dismiss warning"
      >
        ✕
      </button>
    </div>,
    portalRoot
  );
};

export default SessionExpirationWarning;
